import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  KnownDeliveryDtoDeliveryType,
  KnownProjectModuleSettingsDtoNetworkType,
  KnownProjectModuleSettingsDtoStatus
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { RJSFSchema, UiSchema } from '@rjsf/utils';
import { useQueryGetHub, useQueryGetHubDetails } from '../../../lockers/hubs.queries';
import { useMutationBulkEditDeliveryContainerDeliveryTypes } from '../../../lockers/lockers.queries';
import { useQueriesGetProjectModules } from '../../../project-modules/queries';
import {
  mapDeliveryTransactionTypeToProjectModule,
  mapProjectModuleToExternalDeliveryType,
  mapProjectModuleToInternalDeliveryType
} from '../../../project-modules/mappings';
import { deliveryTypeToIconMap } from '../../../deliveries/maps/delivery-type-to-icon.map';
import { DeliveryContainer } from '../../../lockers/models/delivery-container.model';
import { EntityForm } from '../form.types';
import { HubAllocationsForm } from './hub-allocations-form.component';
import { EffectiveModules } from './compute-effective-modules';
import { SettingsFormState } from '../../reducer/settings-tree-reducer.types';
import { SettingsActionDispatcher } from '../../reducer/settings-tree.reducer';

export type HubAllocationsFormInput = {
  hubID: string;
  draft: Record<string, EffectiveModules>;
};

export type HubAllocationsFormOutput = {
  updates: Array<{
    containerID: string;
    selectedInternalProjectModules: string[];
    selectedExternalProjectModules: string[];
  }>;
};

const EMPTY_SCHEMA: RJSFSchema = { type: 'object', properties: {} };
const HIDDEN_UI_SCHEMA: UiSchema = { 'ui:submitButtonOptions': { norender: true } };

export function useEditHubAllocationsForm(
  hubID: string | undefined,
  title: string,
  formState: SettingsFormState | null,
  dispatch: SettingsActionDispatcher
): EntityForm<HubAllocationsFormInput, HubAllocationsFormOutput> | null {
  const hubResult = useQueryGetHub(hubID);
  const hubDetailsResult = useQueryGetHubDetails(hubID);
  const bulkEditMutation = useMutationBulkEditDeliveryContainerDeliveryTypes();

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Prevent selection state from being shared between forms.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [hubID]);

  // After a successful save, wait for the hubDetails refetch to land before wiping the draft —
  // otherwise the UI flashes the stale persisted state for a moment between SUBMIT_FORM_SUCCESS
  // and the React Query invalidation completing. We snapshot dataUpdatedAt at success and clear
  // once it advances.
  const dataUpdatedAtSuccess = useRef<number | null>(null);
  useEffect(() => {
    if (!formState?.isSuccess) {
      dataUpdatedAtSuccess.current = null;
      return;
    }
    if (dataUpdatedAtSuccess.current === null) {
      dataUpdatedAtSuccess.current = hubDetailsResult.dataUpdatedAt ?? 0;
      return;
    }
    if ((hubDetailsResult.dataUpdatedAt ?? 0) > dataUpdatedAtSuccess.current) {
      dataUpdatedAtSuccess.current = null;
      if (formState.nodeId && hubID) {
        dispatch({
          type: 'UPDATE_FORM',
          payload: { nodeId: formState.nodeId, form: { hubID, draft: {} } }
        });
        setSelectedIds(new Set());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState?.isSuccess, hubDetailsResult.dataUpdatedAt]);

  const projectIDs = hubResult.data?.acceptsDeliveriesForProjectIDs ?? [];
  const projectModulesQueries = useQueriesGetProjectModules(projectIDs);
  const modules = useMemo(() => projectModulesQueries.flatMap((result) => result.data || []), [projectModulesQueries]);

  // Match the BE resolver's valid-selection universe: active + internal-network-type only.
  const activeModuleNames = useMemo(
    () => [
      ...new Set(
        modules
          .filter(
            (m) =>
              m.data.status === KnownProjectModuleSettingsDtoStatus.Active &&
              m.data.networkType === KnownProjectModuleSettingsDtoNetworkType.Internal
          )
          .map((m) => m.schema.name)
      )
    ],
    [modules]
  );

  const deliveryContainerMap = useMemo(() => {
    const map = new Map<string, DeliveryContainer>();
    hubDetailsResult.data?.towers?.forEach((tower) => tower.lockers.forEach((locker) => map.set(locker.id, locker)));
    hubDetailsResult.data?.sections?.forEach((section) => section.spaces.forEach((space) => map.set(space.id, space)));
    return map;
  }, [hubDetailsResult.data?.towers, hubDetailsResult.data?.sections]);

  const towerContainerIds = useMemo(() => {
    const map = new Map<string, string[]>();
    hubDetailsResult.data?.towers?.forEach((tower) => {
      const ids = tower.lockers.filter((l) => !l.isKiosk).map((l) => l.id);
      map.set(tower.id, ids);
    });
    hubDetailsResult.data?.sections?.forEach((section) => {
      const ids = section.spaces.filter((s) => !s.isKiosk).map((s) => s.id);
      map.set(section.id, ids);
    });
    return map;
  }, [hubDetailsResult.data?.towers, hubDetailsResult.data?.sections]);

  const transactionTypeProps = useMemo(() => {
    const types = activeModuleNames
      .flatMap((name) => [mapProjectModuleToExternalDeliveryType(name), mapProjectModuleToInternalDeliveryType(name)])
      .filter((t): t is KnownDeliveryDtoDeliveryType => t !== null);
    if (types.length === 0) return undefined;
    return [...new Set(types)].map((t) => ({ value: t, icon: deliveryTypeToIconMap[t] }));
  }, [activeModuleNames]);

  const externalModules = useMemo(
    () => activeModuleNames.filter((n) => mapProjectModuleToExternalDeliveryType(n) !== null),
    [activeModuleNames]
  );

  const internalModules = useMemo(
    () => activeModuleNames.filter((n) => mapProjectModuleToInternalDeliveryType(n) !== null),
    [activeModuleNames]
  );

  // NOTE: The backend has a quirky rule whereby an empty array means "all".
  // The endpoint allows sending no value for internal or external which means "don't change".
  // Only when BOTH arrays are empty on the persisted container do we expand to the full active-module lists.
  // If only one side is empty with the other non-empty, empty means "no modules from that
  // category" — we must NOT fabricate entries there, or the UI will disagree with the BE.
  const originalModulesMap = useMemo(() => {
    const map = new Map<string, EffectiveModules>();
    deliveryContainerMap.forEach((container, id) => {
      const bothEmpty =
        container.selectedInternalProjectModules.length === 0 && container.selectedExternalProjectModules.length === 0;
      const internal = bothEmpty ? [...internalModules] : [...container.selectedInternalProjectModules];
      const external = bothEmpty ? [...externalModules] : [...container.selectedExternalProjectModules];
      map.set(id, { internal, external });
    });
    return map;
  }, [deliveryContainerMap, internalModules, externalModules]);

  const toggleSelection = useCallback(
    (id: string) => {
      const container = deliveryContainerMap.get(id);
      // Kiosks have no delivery types; Disabled lockers are still configurable so their
      // allocations are correct when they come back online.
      if (!container || container.isKiosk) return;
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    },
    [deliveryContainerMap]
  );

  const toggleTowerSelection = useCallback(
    (towerId: string) => {
      const ids = towerContainerIds.get(towerId);
      if (!ids || ids.length === 0) return;
      setSelectedIds((prev) => {
        const allSelected = ids.every((id) => prev.has(id));
        const next = new Set(prev);
        if (allSelected) ids.forEach((id) => next.delete(id));
        else ids.forEach((id) => next.add(id));
        return next;
      });
    },
    [towerContainerIds]
  );

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  if (!hubID) return null;

  return {
    isLoading: hubResult.isLoading || hubDetailsResult.isLoading,
    title,
    subtitle: hubID,
    allowSubtitleCopy: true,
    defaultValues: { hubID, draft: {} },
    initialFormOutput: { updates: [] },
    formSchema: EMPTY_SCHEMA,
    uiSchema: HIDDEN_UI_SCHEMA,
    resetButtonLabel: 'Cancel',
    onReset: () => setSelectedIds(new Set()),
    getOutput: (formData) => {
      const draft = formData?.draft ?? {};
      const updates = Object.entries(draft).map(([containerID, mods]) => ({
        containerID,
        selectedInternalProjectModules: mods.internal,
        selectedExternalProjectModules: mods.external
      }));
      return { updates };
    },
    onSubmit: async (output) => {
      const updates = output?.updates ?? [];
      const payload = { hubID, updates };
      if (payload.updates.length === 0) return;
      await bulkEditMutation.mutateAsync(payload);
      setSelectedIds(new Set());
    },
    renderInsideCard: (formData, { onChange }) => {
      if (!hubDetailsResult.data) return null;

      const draft = formData?.draft ?? {};
      const effectiveModulesMap = new Map<string, EffectiveModules>();
      originalModulesMap.forEach((original, id) => {
        effectiveModulesMap.set(id, draft[id] ?? original);
      });

      const acceptsTransactionType = (lockerId: string, type: string) => {
        const effective = effectiveModulesMap.get(lockerId);
        if (!effective) return false;
        if (effective.internal.length === 0 && effective.external.length === 0) return true;
        const mapping = mapDeliveryTransactionTypeToProjectModule(type as KnownDeliveryDtoDeliveryType);
        if (!mapping) return false;
        return mapping.type === 'internal'
          ? effective.internal.includes(mapping.name)
          : effective.external.includes(mapping.name);
      };

      const setPending = (module: string, type: 'internal' | 'external', action: 'add' | 'remove') => {
        if (selectedIds.size === 0) return;
        const nextDraft = { ...draft };
        selectedIds.forEach((id) => {
          const current = nextDraft[id] ?? originalModulesMap.get(id);
          if (!current) return;
          const key = type === 'internal' ? 'internal' : 'external';
          const list = current[key];
          const updated =
            action === 'add' ? (list.includes(module) ? list : [...list, module]) : list.filter((m) => m !== module);
          nextDraft[id] = { ...current, [key]: updated };
        });
        onChange({ ...formData, draft: nextDraft });
      };

      const setAllPending = (action: 'add' | 'remove') => {
        if (selectedIds.size === 0) return;
        const nextDraft = { ...draft };
        selectedIds.forEach((id) => {
          nextDraft[id] =
            action === 'add'
              ? { internal: [...internalModules], external: [...externalModules] }
              : { internal: [], external: [] };
        });
        onChange({ ...formData, draft: nextDraft });
      };

      return (
        <HubAllocationsForm
          hubDetails={hubDetailsResult.data}
          selectedIds={selectedIds}
          transactionTypeProps={transactionTypeProps}
          externalModules={externalModules}
          internalModules={internalModules}
          deliveryContainerMap={deliveryContainerMap}
          effectiveModulesMap={effectiveModulesMap}
          acceptsTransactionType={acceptsTransactionType}
          onToggleSelection={toggleSelection}
          onToggleTowerSelection={toggleTowerSelection}
          onClearSelection={clearSelection}
          onSetPending={setPending}
          onSetAllPending={setAllPending}
        />
      );
    },
    isLocked: false,
    lockedMessage: null
  };
}
